import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";
import { DemoBadge, Empty, ErrorBox, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";

const Table = ({ head, rows, testId, empty }) => (
  <div className="surface overflow-x-auto">
    {rows.length === 0 ? <div className="p-5"><Empty title={empty} /></div> : (
      <table className="data-table" data-testid={testId}><thead><tr>{head.map((h) => <th key={h}>{h}</th>)}</tr></thead><tbody>{rows}</tbody></table>
    )}
  </div>
);

function Filters({ f, setF, extra }) {
  const pkgs = useFetch("/admin/packages");
  const cashiers = useFetch(extra ? "/admin/cashiers" : null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="surface mb-4 grid gap-2 p-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="filters">
      <select className="field" value={f.package_id || ""} onChange={set("package_id")} data-testid="filter-package"><option value="">All packages</option>{pkgs.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
      {extra && (
        <>
          <select className="field" value={f.cashier_id || ""} onChange={set("cashier_id")} data-testid="filter-cashier"><option value="">All cashiers</option>{cashiers.data?.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}</select>
          <select className="field" value={f.method || ""} onChange={set("method")} data-testid="filter-method"><option value="">All methods</option><option value="CASH">Cash</option><option value="QRPH">QR Ph</option></select>
          <select className="field" value={f.card_status || ""} onChange={set("card_status")} data-testid="filter-card-status"><option value="">All card statuses</option>{["AVAILABLE", "PAYMENT_PENDING", "ACTIVATED", "REGISTERED", "SUSPENDED", "VOIDED"].map((s) => <option key={s}>{s}</option>)}</select>
        </>
      )}
      <input className="field" type="date" value={f.date_from || ""} onChange={set("date_from")} data-testid="filter-date-from" />
      <input className="field" type="date" value={f.date_to || ""} onChange={set("date_to")} data-testid="filter-date-to" />
    </div>
  );
}

const qs = (f) => new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v))).toString();

export function Activations() {
  const [f, setF] = useState({});
  const { data, error, loading } = useFetch(`/admin/activations?${qs(f)}`);
  return (
    <div>
      <PageHead eyebrow="Package activations" title="Activation history" sub="Validity starts on the cashier-confirmed activation date; registration does not extend it." />
      <Filters f={f} setF={setF} />
      <ErrorBox error={error} />
      {loading && !data ? <Spinner /> : (
        <Table testId="activations-table" empty="No activations yet" head={["Card", "Customer", "Package", "Status", "Registered", "Activated", "Expires"]}
          rows={(data || []).map((c) => (
            <tr key={c.id} data-testid={`activation-row-${c.card_serial}`}>
              <td className="font-mono font-semibold">{c.card_serial}</td><td>{c.customer_name}</td><td>{c.package_name}</td>
              <td><StatusBadge status={c.status} /></td><td>{c.registered ? <StatusBadge status="REGISTERED" /> : <span className="text-xs text-slate-400">Not yet</span>}</td>
              <td className="text-xs">{fmtDate(c.activated_at)}</td><td className="text-xs">{fmtDate(c.expires_at)}</td>
            </tr>
          ))} />
      )}
    </div>
  );
}

export function Payments() {
  const [f, setF] = useState({});
  const { data, loading } = useFetch(`/admin/payments?${qs(f)}`);
  const paid = (data || []).filter((p) => p.status === "PAID").reduce((a, p) => a + p.amount, 0);
  return (
    <div>
      <PageHead eyebrow="Payments" title="Cashier transactions" sub={`Collected in view: ${money(paid)}`} />
      <Filters f={f} setF={setF} extra />
      {loading && !data ? <Spinner /> : (
        <Table testId="payments-table" empty="No transactions" head={["Reference", "Receipt", "Method", "Card", "Card status", "Amount", "Status", "Cashier", "Date"]}
          rows={(data || []).map((p) => (
            <tr key={p.id} data-testid={`payment-row-${p.reference}`}>
              <td className="font-mono text-xs font-semibold">{p.reference}</td><td className="font-mono text-xs">{p.receipt?.number || "—"}</td>
              <td className="text-xs">{p.method === "CASH" ? "Cash" : `QR Ph · ${p.mode}`}</td><td className="font-mono text-xs">{p.card_serial}</td>
              <td>{p.card_status && <StatusBadge status={p.card_status} />}</td><td className="font-semibold">{money(p.amount, p.currency)}</td>
              <td><StatusBadge status={p.status} /></td><td className="text-xs">{p.cashier_name || p.cashier_username}</td><td className="text-xs">{fmtDateTime(p.created_at)}</td>
            </tr>
          ))} />
      )}
    </div>
  );
}

export function ServiceRequests() {
  const { data, loading, reload } = useFetch("/admin/service-requests");
  const act = async (id, action) => {
    const reason = action === "CANCEL" ? window.prompt("Cancellation reason") : null;
    if (action === "CANCEL" && !reason) return;
    try { await api.patch(`/admin/service-requests/${id}`, { action, reason }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading && !data) return <Spinner />;
  return (
    <div>
      <PageHead eyebrow="Service requests" title="Service requests & appointments" />
      <Table testId="service-requests-table" empty="No service requests" head={["Service", "Beneficiary", "Customer", "Provider", "Schedule", "Status", ""]}
        rows={(data || []).map((r) => (
          <tr key={r.id} data-testid={`sr-row-${r.id}`}>
            <td className="font-semibold">{r.benefit_name} {r.is_demo && <DemoBadge />}</td>
            <td className="text-xs">{r.beneficiary_name} · {r.beneficiary_relationship}</td><td className="font-mono text-xs">{r.customer_username}</td>
            <td className="text-xs">{r.provider_name}</td><td className="text-xs">{fmtDateTime(r.scheduled_at)}</td><td><StatusBadge status={r.status} /></td>
            <td className="whitespace-nowrap">
              {r.status === "REQUESTED" && <button className="btn-ghost btn-sm" onClick={() => act(r.id, "CONFIRM")} data-testid={`sr-confirm-${r.id}`}>Confirm</button>}
              {["REQUESTED", "CONFIRMED"].includes(r.status) && <button className="btn-ghost btn-sm text-red-600" onClick={() => act(r.id, "CANCEL")} data-testid={`sr-cancel-${r.id}`}>Cancel</button>}
            </td>
          </tr>
        ))} />
    </div>
  );
}
